// Video decoding (WebCodecs via mediabunny) and per-frame ML depth.

import { ALL_FORMATS, BlobSource, CanvasSink, Input } from 'mediabunny';
import { fitSize } from '../image';
import { estimateDepth } from '../ml/client.svelte';
import { toDepth16, type Depth16, type RgbaData } from '../types';

export interface VideoInfo {
  name: string;
  width: number;
  height: number;
  duration: number;
}

export async function probeVideo(file: File): Promise<VideoInfo> {
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) throw new Error('no video track');
    if (!(await track.canDecode())) throw new Error(`this browser cannot decode ${track.codec ?? 'this codec'}`);
    const first = await track.getFirstTimestamp();
    const end = await track.computeDuration();
    return {
      name: file.name,
      width: track.displayWidth,
      height: track.displayHeight,
      duration: Math.max(0, end - first),
    };
  } finally {
    input.dispose();
  }
}

/**
 * Samples `frames` frames at `fps` from the start of the video, estimates
 * depth for each (sequentially; the model owns the GPU) and returns
 * depth frames plus downscaled colour frames (for the "from source" texture).
 */
export async function videoToDepth(
  file: File,
  fps: number,
  frames: number,
  colourSide: number,
  onFrame: (done: number) => void,
  signal?: AbortSignal,
): Promise<{ depth: Depth16[]; colour: RgbaData[] }> {
  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) throw new Error('no video track');
    const first = await track.getFirstTimestamp();
    // Frames at ~1 megapixel are plenty for a 518 px model input.
    const [w, h] = fitSize(track.displayWidth, track.displayHeight, 1024, 1024);
    const [cw, ch] = fitSize(track.displayWidth, track.displayHeight, colourSide, colourSide, true);
    const sink = new CanvasSink(track, { width: w, height: h, fit: 'fill', poolSize: 2 });
    const timestamps = Array.from({ length: frames }, (_, i) => first + i / fps);
    const depth: Depth16[] = [];
    const colour: RgbaData[] = [];
    let last: RgbaData | null = null;
    for await (const wrapped of sink.canvasesAtTimestamps(timestamps)) {
      if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
      let img: RgbaData | null = last;
      if (wrapped) {
        const ctx = (wrapped.canvas as HTMLCanvasElement | OffscreenCanvas).getContext('2d', {
          willReadFrequently: true,
        }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
        const id = ctx.getImageData(0, 0, w, h);
        img = { width: w, height: h, data: id.data };
        const small = new OffscreenCanvas(cw, ch);
        small.getContext('2d')!.drawImage(wrapped.canvas as CanvasImageSource, 0, 0, cw, ch);
        colour.push({ width: cw, height: ch, data: small.getContext('2d')!.getImageData(0, 0, cw, ch).data });
      } else if (last) {
        colour.push(colour[colour.length - 1]);
      }
      if (!img) continue;
      last = img;
      depth.push(toDepth16(await estimateDepth(img)));
      onFrame(depth.length);
    }
    return { depth, colour };
  } finally {
    input.dispose();
  }
}
